import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, TrendUp, Users, ChartBar, Lightning, ArrowRight, Target, Scales, EnvelopeSimple } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug } from "@/lib/playerSlug";
import EmailCaptureModal from "@/components/EmailCaptureModal";

const POS_COLORS = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

function StatCard({ label, value, sub }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-4 text-center">
      <p className="font-mono font-bold text-2xl text-[#ccff00]">{value}</p>
      <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mt-1">{label}</p>
      {sub && <p className="text-[10px] text-[#666] mt-0.5">{sub}</p>}
    </div>
  );
}

function InsightBar({ label, pct, color, sub }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#a3a3a3]">{label}</span>
        <span className="text-xs font-mono font-bold text-white">{pct}%</span>
      </div>
      <div className="h-5 bg-[#1a1a1a] rounded-sm overflow-hidden">
        <div className="h-full rounded-sm transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color || "#ccff00" }} />
      </div>
      {sub && <p className="text-[9px] text-[#555] mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="font-heading text-2xl text-white uppercase mb-4 pb-2 border-b border-[#262626]">{title}</h2>
      {children}
    </section>
  );
}

export default function ChampionshipReport() {
  const [data, setData] = useState(null);
  const [deep, setDeep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/championship/blueprint`),
          axios.get(`${API}/championship/deep-analysis`),
        ]);
        setData(r1.data);
        setDeep(r2.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-4">
        <div className="h-10 w-96 skeleton rounded-sm" />
        <div className="h-64 skeleton rounded-sm" />
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="font-heading text-3xl text-white uppercase mb-4">Championship Report</h1>
        <p className="text-[#a3a3a3] mb-6">Championship data is being processed. Please check back shortly.</p>
        <Link to="/research" className="text-[#ccff00] text-sm no-underline hover:underline">Back to Research</Link>
      </div>
    </div>
  );

  const all = deep?.segments?.all || {};
  const sf = deep?.segments?.sf || {};
  const oneqb = deep?.segments?.oneqb || {};
  const total = data.total_champions;
  const topPlayers = data.top_players_on_rosters || [];
  const topStarters = data.top_starters || [];
  const da = data.deeper_analysis || {};

  const chartData = topPlayers.slice(0, 20).map(p => ({
    name: p.name.split(" ").pop(), fullName: p.name, pct: p.pct, position: p.position,
  }));

  const allocData = Object.entries(all.positional_value_allocation || {}).map(([pos, pct]) => ({
    name: pos, value: pct, fill: POS_COLORS[pos] || "#ccff00",
  }));

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="What It Takes to Win: 6,976 Championship Rosters Analyzed (2025) — Dynasty Trade Maker"
        description="We analyzed 6,976 dynasty fantasy football championship rosters from the 2025 Sleeper season. Positional allocation, elite player concentration, age profiles, QB/TE findings, and the most-rostered players on title teams."
        path="/research/championship-report"
      />
      <PublicNav />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-6" data-testid="report-hero">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">ORIGINAL RESEARCH — {total.toLocaleString()} DYNASTY LEAGUES</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase mb-4">
            WHAT IT TAKES TO <span className="text-[#ccff00]">WIN</span>
          </h1>
        </div>
        <div className="mb-8 border border-[#262626] rounded-sm overflow-hidden" data-testid="hero-image">
          <img src="/hero-images/championship-report.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>
        <div className="text-center mb-10">
          <p className="text-base text-[#a3a3a3] max-w-2xl mx-auto mb-4">We analyzed {total.toLocaleString()} dynasty league championship rosters from the 2025 Sleeper season — {data.sf_leagues.toLocaleString()} Superflex, {data.oneqb_leagues.toLocaleString()} 1QB — to identify what winning teams actually look like.</p>
          <p className="text-xs text-[#666]">2025 Season Edition &middot; Dynasty Trade Maker Research</p>
        </div>

        {/* Table of Contents */}
        <div className="border border-[#262626] bg-[#171717] p-4 mb-10">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mb-2">IN THIS REPORT</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {[["#key-findings","Key Findings"],["#positional-allocation","Positional Allocation"],["#elite-player-concentration","Elite Player Tiers"],["#age-profile","Age Profile"],["#league-winners","Top Championship Players"],["#methodology","Methodology"]].map(([href, label]) => (
              <a key={href} href={href} className="text-xs text-[#a3a3a3] hover:text-[#ccff00] no-underline py-1">{label}</a>
            ))}
          </div>
        </div>

        {/* Key Findings */}
        <Section id="key-findings" title="KEY FINDINGS">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard label="Avg Roster Value" value={all.avg_value?.toLocaleString()} />
            <StatCard label="Top-3 Concentration" value={`${all.top3_concentration}%`} sub="of total roster value" />
            <StatCard label="Avg Roster Age" value={all.avg_roster_age} sub="years" />
            <StatCard label="Top-30 WRs" value={all.avg_wr_top30} sub="per champion" />
          </div>
          <div className="space-y-3 mb-6">
            <InsightBar label="Had a top-10 RB" pct={all.rb_tiers?.top10 || 0} color={POS_COLORS.RB} />
            <InsightBar label="Had a top-10 WR" pct={da.pct_with_top10_wr || 0} color={POS_COLORS.WR} />
            <InsightBar label="Had a top-10 QB" pct={all.qb_tiers?.top10 || 0} color={POS_COLORS.QB} />
            <InsightBar label="Had a top-5 TE" pct={all.te_tiers?.top5 || 0} color={POS_COLORS.TE} />
          </div>
          <p className="text-sm text-[#a3a3a3] leading-relaxed">The most striking finding: <strong className="text-white">{all.rb_tiers?.top10 || 0}% of champions had a top-10 dynasty running back</strong> on their roster. Wide receiver depth matters too — champions averaged {all.avg_wr_top30} WRs ranked in the top 30, suggesting a "multiple WR2s" approach outperforms concentrating on a single elite receiver.</p>
        </Section>

        {/* Positional Allocation */}
        <Section id="positional-allocation" title="HOW CHAMPIONS ALLOCATE ROSTER VALUE">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">Championship rosters allocated value heavily toward WR ({all.positional_value_allocation?.WR || 0}%) and RB ({all.positional_value_allocation?.RB || 0}%), with QB at {all.positional_value_allocation?.QB || 0}% and TE at {all.positional_value_allocation?.TE || 0}%.</p>
              <div className="space-y-2">
                {allocData.map(d => (
                  <div key={d.name} className="flex items-center gap-3">
                    <span className="text-sm font-bold w-8" style={{ color: d.fill }}>{d.name}</span>
                    <div className="flex-1 h-6 bg-[#1a1a1a] rounded-sm overflow-hidden">
                      <div className="h-full rounded-sm" style={{ width: `${d.value * 2.5}%`, backgroundColor: d.fill }} />
                    </div>
                    <span className="text-sm font-mono text-white w-12 text-right">{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mb-2">SUPERFLEX vs 1QB</p>
              <div className="border border-[#262626] overflow-hidden text-xs mb-3">
                <div className="grid grid-cols-3 bg-[#0a0a0a] px-3 py-1.5 border-b border-[#262626]">
                  <span className="text-[#555]">Position</span><span className="text-[#555] text-right">SF</span><span className="text-[#555] text-right">1QB</span>
                </div>
                {["QB","RB","WR","TE"].map(pos => (
                  <div key={pos} className="grid grid-cols-3 px-3 py-1.5 border-b border-[#1a1a1a]">
                    <span style={{ color: POS_COLORS[pos] }}>{pos}</span>
                    <span className="text-white text-right">{sf.positional_value_allocation?.[pos] || 0}%</span>
                    <span className="text-[#a3a3a3] text-right">{oneqb.positional_value_allocation?.[pos] || 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deep takeaways */}
          <div className="border border-[#ccff00]/20 bg-[#ccff00]/5 p-5 mb-4">
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-3">WHAT THIS MEANS FOR YOUR ROSTER</p>
            <div className="space-y-3">
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#45b7d1] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">WR is the foundation, not RB.</strong> Despite the hype around elite running backs, champions spent more roster capital on wide receivers ({all.positional_value_allocation?.WR || 0}% vs {all.positional_value_allocation?.RB || 0}%). WRs have longer careers and more stable values, making them the safest long-term investment.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#ff6b6b] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">The QB gap is format-specific.</strong> In 1QB leagues, champions spent just {oneqb.positional_value_allocation?.QB || 0}% on QB — meaning you can win with a mid-tier QB. In Superflex, QB jumps to {sf.positional_value_allocation?.QB || 0}%. If you are in SF and your QB room is weak, you are fighting an uphill battle.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#f9ca24] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">TE premium is real but capped.</strong> At {all.positional_value_allocation?.TE || 0}%, champions spent roughly 1/3 what they spent on RB. A top TE helps but overpaying for TE at the expense of RB/WR depth is a losing strategy. Get one good TE and focus elsewhere.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#4ecdc4] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">1QB champions lean harder into WR.</strong> With {oneqb.positional_value_allocation?.WR || 0}% vs SF's {sf.positional_value_allocation?.WR || 0}%, 1QB winners poured the savings from not paying for a second elite QB directly into their receiver room.</p></div>
            </div>
          </div>
        </Section>

        {/* Elite Player Concentration */}
        <Section id="elite-player-concentration" title="THE ELITE PLAYER QUESTION">
          <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">Do you need elite players to win? The data says yes — but <strong className="text-white">which position matters most</strong> depends on format.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
            <div className="border border-[#262626] bg-[#171717] p-4">
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-3">SUPERFLEX ({sf.total?.toLocaleString()} leagues)</p>
              <div className="space-y-2">
                <InsightBar label="Had top-5 QB" pct={sf.qb_tiers?.top5 || 0} color={POS_COLORS.QB} />
                <InsightBar label="Had top-5 RB" pct={sf.rb_tiers?.top5 || 0} color={POS_COLORS.RB} />
                <InsightBar label="Had top-5 TE" pct={sf.te_tiers?.top5 || 0} color={POS_COLORS.TE} />
              </div>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-4">
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-3">1QB ({oneqb.total?.toLocaleString()} leagues)</p>
              <div className="space-y-2">
                <InsightBar label="Had top-5 QB" pct={oneqb.qb_tiers?.top5 || 0} color={POS_COLORS.QB} />
                <InsightBar label="Had top-5 RB" pct={oneqb.rb_tiers?.top5 || 0} color={POS_COLORS.RB} />
                <InsightBar label="Had top-5 TE" pct={oneqb.te_tiers?.top5 || 0} color={POS_COLORS.TE} />
              </div>
            </div>
          </div>
          <p className="text-sm text-[#a3a3a3] leading-relaxed">Champions concentrated <strong className="text-white">{all.top3_concentration}%</strong> of their total roster value in their top 3 players — roughly a third of all value in three assets. The "stars and scrubs" approach has real support in the data.</p>
          
          <div className="border border-[#ccff00]/20 bg-[#ccff00]/5 p-5 mt-4">
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-3">STRATEGIC TAKEAWAYS</p>
            <div className="space-y-3">
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#ccff00] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">Consolidate, don't spread.</strong> Trading two solid pieces for one elite player is almost always the right move. Champions won with a handful of stars, not a deep roster of average players. If you can 2-for-1 up to an elite asset, do it.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#ccff00] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">RBs are the highest-floor path to a ring.</strong> {all.rb_tiers?.top10 || 0}% of champions had a top-10 RB — the single highest correlation. Even though RBs depreciate fast, having one during your contention window is essential.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#ccff00] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">The SF QB advantage is massive.</strong> {sf.qb_tiers?.top5 || 0}% of SF champions had a top-5 QB. In 1QB, only {oneqb.qb_tiers?.top5 || 0}% needed one — nearly the same! The difference is that in SF, having TWO good QBs is the real edge. {sf.qb_tiers?.top12 || 0}% had a QB12 or better.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#ccff00] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">The TE market is inefficient.</strong> {all.te_tiers?.top5 || 0}% of champions had a top-5 TE — and those top TEs are far cheaper than top RBs. If you can acquire a top-5 TE without sacrificing your RB/WR core, the data says it is worth it.</p></div>
            </div>
          </div>
        </Section>

        {/* Age Profile */}
        <Section id="age-profile" title="THE AGE OF A CHAMPION">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Avg Roster Age" value={all.avg_roster_age} />
            <StatCard label="Avg Starter Age" value={all.avg_starter_age} />
            <StatCard label="Avg QBs/Roster" value={all.avg_positions?.QB || 0} sub={`${all.avg_positions?.RB || 0} RB, ${all.avg_positions?.WR || 0} WR, ${all.avg_positions?.TE || 0} TE`} />
            <StatCard label="Top-3 Value Share" value={`${all.top3_concentration}%`} sub="in 3 players" />
          </div>
          <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">Championship rosters averaged <strong className="text-white">{all.avg_roster_age} years old</strong> across all rostered players and <strong className="text-white">{all.avg_starter_age} among starters</strong>. This is slightly older than expected — suggesting that the "youth at all costs" approach is not what actually wins titles. Championship teams balance youth with proven production.</p>
          
          <div className="border border-[#ccff00]/20 bg-[#ccff00]/5 p-5">
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-3">AGE INSIGHTS</p>
            <div className="space-y-3">
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#4ecdc4] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">Starters are older than rosters.</strong> The {(parseFloat(all.avg_starter_age) - parseFloat(all.avg_roster_age)).toFixed(1)}-year gap between starter age ({all.avg_starter_age}) and roster age ({all.avg_roster_age}) tells you champions carry young depth but start experienced players. The bench is where you stash youth; the lineup is where you start winners.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#ff6b6b] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">Do not panic-sell aging stars.</strong> At {all.avg_roster_age} average, plenty of 28-30 year olds were on title rosters. If a player is producing, their age does not disqualify them from a championship. Sell when production drops, not when the birthday hits.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#45b7d1] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">The sweet spot is 25-29.</strong> Champions leaned on prime-age players who are old enough to be proven but young enough to sustain production. Targeting players entering this window is the highest-EV move in dynasty.</p></div>
              <div className="flex items-start gap-2"><Target size={14} weight="bold" className="text-[#f9ca24] mt-0.5 shrink-0" /><p className="text-sm text-[#a3a3a3]"><strong className="text-white">Full rebuilds can still win.</strong> You do not need a 5-year-old average roster to compete. A rebuild that lands 2-3 elite young assets and supplements with mid-20s production can contend faster than a team hoarding rookies.</p></div>
            </div>
          </div>
        </Section>

        {/* Top Championship Players */}
        <Section id="league-winners" title="THE LEAGUE WINNERS: MOST COMMON CHAMPIONSHIP PLAYERS">
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 90 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: "#666" }} domain={[0, 30]} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#a3a3a3" }} width={85} />
              <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", fontSize: 12 }} formatter={(v, n, props) => [`${v}% of champions`, props.payload.fullName]} />
              <Bar dataKey="pct" radius={[0, 2, 2, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={POS_COLORS[e.position] || "#ccff00"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {["QB","RB","WR","TE"].map(pos => (
              <div key={pos} className="border border-[#262626] bg-[#171717] p-3">
                <p className="text-[9px] font-bold uppercase tracking-wider mb-2" style={{ color: POS_COLORS[pos] }}>TOP {pos}s</p>
                {topPlayers.filter(p => p.position === pos).slice(0, 5).map((p, i) => (
                  <Link key={i} to={`/players/${playerToSlug(p.name)}`} className="flex justify-between py-0.5 no-underline hover:bg-[#1a1a1a] px-1 rounded-sm">
                    <span className="text-[11px] text-white">{p.name.split(" ").pop()}</span>
                    <span className="text-[11px] font-mono text-[#ccff00]">{p.pct}%</span>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </Section>

        {/* Methodology */}
        <Section id="methodology" title="METHODOLOGY">
          <div className="text-sm text-[#a3a3a3] leading-relaxed space-y-3">
            <p><strong className="text-white">Dataset:</strong> {total.toLocaleString()} completed dynasty leagues from Sleeper, 2025 season. {data.sf_leagues.toLocaleString()} Superflex, {data.oneqb_leagues.toLocaleString()} 1QB.</p>
            <p><strong className="text-white">Collection method:</strong> Snowball sampling starting from Dynasty Trade Maker users' Sleeper leagues. For each league, we identified all owners and discovered their other leagues, repeating until reaching our sample target.</p>
            <p><strong className="text-white">Champion identification:</strong> Sleeper's winners bracket API, identifying the roster with placement = 1 (league champion).</p>
            <p><strong className="text-white">Player values:</strong> FantasyCalc consensus dynasty values at time of analysis, used for positional allocation and tier classifications.</p>
            <p><strong className="text-white">Limitations:</strong> Sample is weighted toward active Sleeper dynasty managers (more engaged than average). Results reflect associations, not causation — having a top-5 QB correlates with winning, but doesn't guarantee it.</p>
          </div>
        </Section>

        {/* PDF Lead Magnet */}
        <div className="border border-[#262626] bg-[#171717] p-5 text-center mb-8" data-testid="pdf-capture">
          <EnvelopeSimple size={24} className="text-[#ccff00] mx-auto mb-2" weight="bold" />
          <h3 className="font-heading text-lg text-white uppercase mb-2">GET THE FULL REPORT AS A PDF</h3>
          <p className="text-xs text-[#888] mb-3">Download the complete championship analysis — printable, shareable, and formatted for easy reference.</p>
          <button onClick={() => setShowPdfModal(true)} className="h-9 px-6 btn-primary rounded-sm text-[11px] font-bold uppercase" data-testid="pdf-download-btn">
            DOWNLOAD PDF EDITION
          </button>
        </div>
        <EmailCaptureModal open={showPdfModal} onClose={() => setShowPdfModal(false)} source="championship-report-pdf" />

        {/* CTA */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center mb-8">
          <h3 className="font-heading text-lg text-white uppercase mb-2">HOW DOES YOUR ROSTER COMPARE?</h3>
          <p className="text-xs text-[#888] mb-4">Import your Sleeper league to see how your roster stacks up against {total.toLocaleString()} championship-winning teams.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold"><Lightning size={12} weight="fill" /> IMPORT YOUR LEAGUE</a>
            <Link to="/championship-blueprint" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Quick Blueprint <ArrowRight size={11} /></Link>
          </div>
        </div>

        {/* Deep Dives */}
        <div className="mb-8" data-testid="deep-dives">
          <h2 className="font-heading text-2xl text-white uppercase mb-4 pb-2 border-b border-[#262626]">DEEP DIVES</h2>
          <p className="text-sm text-[#a3a3a3] mb-4">Each article below takes one question from this report and answers it in depth, with format-specific breakdowns and actionable takeaways.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { href: "/research/qb-question", title: "Do You Need a Top-5 QB to Win?", stat: `${all.qb_tiers?.top5 || 0}% of champions had one` },
              { href: "/research/league-winners", title: "The 2025 League Winners", stat: "Top 25 players on championship rosters" },
              { href: "/research/stars-and-scrubs", title: "Stars and Scrubs vs Depth", stat: `${all.top3_concentration || 0}% of value in top 3 players` },
              { href: "/research/age-of-champion", title: "The Age of a Champion", stat: `Avg roster age: ${all.avg_roster_age || 0}` },
              { href: "/research/elite-te-question", title: "Do Champions Pay Up at TE?", stat: `${all.te_tiers?.top5 || 0}% had a top-5 TE` },
              { href: "/research/positional-blueprint", title: "Positional Blueprint", stat: `WR ${all.positional_value_allocation?.WR || 0}%, RB ${all.positional_value_allocation?.RB || 0}%` },
            ].map(item => (
              <Link key={item.href} to={item.href} className="flex items-center justify-between p-4 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline transition-colors rounded-sm">
                <div>
                  <p className="text-sm text-white font-semibold">{item.title}</p>
                  <p className="text-[10px] text-[#666]">{item.stat}</p>
                </div>
                <ArrowRight size={14} className="text-[#555] shrink-0" />
              </Link>
            ))}
          </div>
        </div>

        <div className="text-center text-[10px] text-[#444] space-x-3">
          <Link to="/championship-blueprint" className="hover:text-[#a3a3a3] no-underline">Blueprint</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
        </div>
      </div>
    </div>
  );
}
